import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, of, delay, throwError, map } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ServiceLoginResponse {
  error: string;
  code: string;
  message: number;
  response: LoginResponse;
}

export interface LoginResponse {
  token: string;
  type: number;
  user: User;
}

export interface User {
  id: number;
  email: string;
  name: string;
  apellidos: string;
  role: string;
  foto?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // 🔧 USUARIOS MOCK PARA DESARROLLO
  private readonly MOCK_USERS = [
    {
      email: 'sistema@job360.cl',
      password: 'Sistema123!',
      user: {
        id: 1,
        email: 'sistema@job360.cl',
        name: 'Usuario',
        apellidos: 'Sistema',
        role: 'SISTEMA'
      }
    },
    {
      email: 'rrhh@job360.cl',
      password: 'Rrhh123!',
      user: {
        id: 2,
        email: 'rrhh@job360.cl',
        name: 'Usuario',
        apellidos: 'RRHH',
        role: 'RRHH'
      }
    },
    {
      email: 'comercial@job360.cl',
      password: 'Comercial123!',
      user: {
        id: 3,
        email: 'comercial@job360.cl',
        name: 'Usuario',
        apellidos: 'Comercial',
        role: 'COMERCIAL'
      }
    }
  ];

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Verificar si hay un usuario logueado al inicializar el servicio
    this.checkStoredAuth();
  }

  /**
   * Verifica si hay información de autenticación almacenada
   */
  private checkStoredAuth(): void {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        this.logout();
      }
    }
  }

  /**
   * Realizar login
   */
  login(email: string, password: string): Observable<LoginResponse> {
    // 🔧 MODO DESARROLLO: Usar datos mock (cuando useMockAuth = true)
    if (environment.useMockAuth) {
      console.log('🔧 [DEMO] Usando autenticación MOCK');
      return this.loginMock(email, password);
    }

    // MODO PRODUCCIÓN: Usar API real
    const loginData: LoginRequest = { email, password };
    return this.http.post<ServiceLoginResponse>(`${this.apiUrl}/admin/auth`, loginData)
      .pipe(
        tap(response => {
          // Guardar token y datos del usuario
          localStorage.setItem('token', response.response.token);
          localStorage.setItem('user', JSON.stringify(response.response.user));

          // Actualizar el subject con el usuario actual
          this.currentUserSubject.next(response.response.user);
        }),
        // Usamos map para transformar el valor emitido por el Observable
        map(response => {
          // Devolvemos solo el sub-objeto 'response' que contiene LoginResponse
          return response.response;
        })
      );
  }

  /**
   * 🔧 Login MOCK para desarrollo (sin backend)
   */
  private loginMock(email: string, password: string): Observable<LoginResponse> {
    const mockUser = this.MOCK_USERS.find(u => u.email === email && u.password === password);

    if (!mockUser) {
      console.error('❌ Credenciales incorrectas para:', email);
      return throwError(() => new Error('Credenciales incorrectas'));
    }

    const mockResponse: LoginResponse = {
      token: 'mock-token-' + Date.now(),
      type: 1,
      user: mockUser.user
    };

    // Simular delay de red
    return of(mockResponse).pipe(
      delay(800),
      tap(response => {
        console.log('✅ Login MOCK exitoso:', response.user);
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        this.currentUserSubject.next(response.user);
      })
    );
  }

  /**
   * Registro de usuario
   */
  register(userData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, userData);
  }

  /**
   * Recuperación de contraseña
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/forgot`, { email });
  }

  /**
   * Verificar Codigo
   */
  verifyCode(code: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/admin/verify_code`,
      { code }
    );
  }
  resetPassword(code: string, password: string, passwordRepeat: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/admin/reset`,
      { code, password, passwordRepeat }
    );
  }
  /**
   * Cerrar sesión
   */
  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token;
  }

  /**
   * Obtener el token actual
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Obtener el usuario actual
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Navegar al dashboard según el rol del usuario
   */
  navigateToDashboard(): void {
    const user = this.getCurrentUser();

    if (!user) {
      this.router.navigate(['/auth/login']);
      return;
    }

    // Redirigir según el rol
    switch (user.role.toUpperCase()) {
      case 'ADMIN':
        this.router.navigate(['/dashboard/admin']);
        break;
      case 'EMPRESA':
        this.router.navigate(['/dashboard/empresa']);
        break;
      case 'CANDIDATO':
        this.router.navigate(['/dashboard/candidato']);
        break;
      default:
        this.router.navigate(['/dashboard']);
        break;
    }
  }
}
